import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { PkgStatus, PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { AppSelect } from '@/components/AppSelect'
import FilterIcon from '@/components/icons/filter.svg?react'
import PkgUniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer'
import { WarehouseFilter } from '@/components/WarehouseFilter'
import { useIsMobile } from '@/hooks/useIsMobile'
import { RoutePath } from '@/RoutePath'

export enum ViewEnum {
  All = 'all',
  Shipped = 'shipped',
  Identify = 'identify',
}

export interface ClientPkgFilterState {
  warehouses: PkgWarehouse[]
  status: string | 'all'
}
interface Props {
  warehouses: PkgWarehouse[]
  value: ClientPkgFilterState
  onChange: (value: ClientPkgFilterState) => void
  currentView: ViewEnum
  statusOptions: string[]
  isUniteShown: boolean
}

export const ClientPkgsFilter = ({ warehouses, value, onChange, currentView, statusOptions, isUniteShown }: Props) => {
  const { t } = useTranslation()
  const statusOptionsToShow = useMemo(() => {
    return [{ label: t('filter.all'), value: 'all' }, ...statusOptions.map(status => ({ label: status, value: status }))]
  }, [statusOptions, t])
  const isMobile = useIsMobile()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  return (
    <div className='flex w-full justify-between gap-6 md:flex-col'>
      {!isMobile && (
        <div className='flex flex-wrap items-center justify-between gap-4'>
          {currentView === ViewEnum.All && (
            <AppSelect
              selectValueComponent={
                <span className='text-text-secondary'>
                  {t('filter.status')}: {statusOptionsToShow.find(option => option.value === value.status)?.label}
                </span>
              }
              triggerClassName='min-w-[180px]'
              options={statusOptionsToShow}
              value={value.status}
              onChange={newVal => onChange({ ...value, status: newVal as 'all' | PkgStatus })}
            />
          )}
          {warehouses.length > 1 && (
            <WarehouseFilter
              warehouses={warehouses}
              value={value.warehouses}
              onChange={newVal => onChange({ ...value, warehouses: newVal })}
            />
          )}
        </div>
      )}
      {currentView === 'all' && isUniteShown && (
        <Link to={RoutePath.ClientPkgUnite} className='self-start'>
          <Button
            icon={<PkgUniteIcon className='size-6' />}
            variant='secondary'
            size='sm'
            className='border-border-default h-7 gap-2 self-start text-sm font-normal shadow-xs hover:bg-white disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none md:h-9'
          >
            {t('filter.unitePackages')}
          </Button>
        </Link>
      )}
      {isMobile && currentView === ViewEnum.All && warehouses.length > 1 && (
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerTrigger asChild>
            <Button className='h-7 text-sm' icon={<FilterIcon className='text-icon-default' />} variant='secondary' size='sm'>
              {t('filter.filterPackages')}
            </Button>
          </DrawerTrigger>
          <DrawerContent className='min-h-[40vh]'>
            <DrawerHeader>
              <DrawerTitle>{t('filter.filterPackagesTitle')}</DrawerTitle>
            </DrawerHeader>
            {warehouses.length > 1 && (
              <div className='mb-6 flex grow flex-col gap-4'>
                <p className='text-sm font-medium'>{t('filter.selectCountries')}</p>
                <WarehouseFilter
                  warehouses={warehouses}
                  value={value.warehouses}
                  onChange={newVal => onChange({ ...value, warehouses: newVal })}
                />
              </div>
            )}
            {currentView === ViewEnum.All && (
              <div className='mb-6 flex grow flex-col gap-4'>
                <p className='text-sm font-medium'>{t('filter.packageStatus')}</p>
                <AppSelect
                  className='w-fit'
                  selectValueComponent={
                    <span className='text-text-secondary'>
                      סטטוס: {statusOptionsToShow.find(option => option.value === value.status)?.label}
                    </span>
                  }
                  triggerClassName='min-w-[180px]'
                  options={statusOptionsToShow}
                  value={value.status}
                  onChange={newVal => onChange({ ...value, status: newVal as 'all' | PkgStatus })}
                />
              </div>
            )}
            <Button className='w-full' size='lg' onClick={() => setIsDrawerOpen(false)}>
              {t('filter.applyFilter')}
            </Button>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  )
}
